import React, { useState } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { Check, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { SteppedModalProps, StepIndicatorProps } from './SteppedModal.types';

const StepIndicator: React.FC<StepIndicatorProps> = ({ totalSteps, currentStep }) => {
  return (
    <div className="flex flex-col items-center gap-2.5">
      <div className="flex items-center">
        {Array.from({ length: totalSteps }).map((_, index) => {
          const isComplete = index < currentStep;
          const isCurrent = index === currentStep;
          const showConnector = index < totalSteps - 1;
          return (
            <React.Fragment key={index}>
              <div
                className={[
                  'flex items-center justify-center h-7 w-7 rounded-full text-[11px] font-semibold transition-all duration-200',
                  isComplete
                    ? 'bg-primary text-zinc-900'
                    : isCurrent
                      ? 'bg-primary text-zinc-900 ring-4 ring-primary/20'
                      : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200',
                ].join(' ')}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>
              {showConnector && (
                <div
                  className={`h-px w-10 transition-colors duration-200 ${
                    isComplete ? 'bg-primary' : 'bg-foreground/15'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Typography variant="small" className="text-muted-foreground tracking-wide">
        Step {currentStep + 1} of {totalSteps}
      </Typography>
    </div>
  );
};

export const SteppedModal: React.FC<SteppedModalProps> = ({
  isOpen,
  onClose,
  title,
  headerTitle,
  children,
  currentStep: externalStep,
  onNext: externalOnNext,
  onPrevious: externalOnPrevious,
  onComplete,
  onReset: externalReset,
  showReset = true,
  size = 'base',
  disableNext = false,
}) => {
  const [internalStep, setInternalStep] = useState(0);

  // Use external or internal state based on whether external control is provided
  const currentStep = typeof externalStep === 'number' ? externalStep : internalStep;
  const totalSteps = React.Children.count(children);

  const handleNext = () => {
    if (externalOnNext) {
      externalOnNext();
    } else {
      setInternalStep((prev) => Math.min(prev + 1, totalSteps - 1));
    }
  };

  const handleReset = () => {
    if (externalReset) {
      externalReset();
    } else {
      setInternalStep(0);
    }
  };

  const handlePrevious = () => {
    if (externalOnPrevious) {
      externalOnPrevious();
    } else {
      setInternalStep((prev) => Math.max(prev - 1, 0));
    }
  };

  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size}>
      <Modal.Header>
        <div className="flex justify-between items-center w-full gap-5">
          <Typography variant="h3" className="text-lg font-semibold tracking-tight">
            {headerTitle}
          </Typography>
          {showReset && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </Button>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="relative flex flex-col gap-8 ask-remi-gradient">
          <div className="space-y-4">
            <Typography
              variant="h2"
              className="text-base font-semibold text-center text-foreground"
            >
              {title}
            </Typography>
            <StepIndicator totalSteps={totalSteps} currentStep={currentStep} />
          </div>
          <div className="text-xs pt-1">{React.Children.toArray(children)[currentStep]}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-between w-full">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <Button
            onClick={isLastStep ? onComplete : handleNext}
            disabled={disableNext}
            data-tour={isLastStep ? 'modal-complete-button' : 'modal-next-button'}
            className="gap-1.5"
          >
            {isLastStep ? 'Complete' : 'Next'}
            {!isLastStep && <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
